import { IconButton, Stack, TextField, Typography } from '@mui/material'
import { SmartDialog } from '../../generic/utilities/SmartDialog'
import { useEffect, useMemo, useReducer, useState, useCallback } from 'react'
import { apiGet, apiPost } from '../../generic/Api_Functions'
import SmartSelect from '../../generic/smartSelect/SmartSelect'
import { getResourcesByType, getResourceTypes } from '../../management/SetResourceUtils'
import { StorageRounded } from '@mui/icons-material'
import Fixture from '../fixture/Fixture'
import Tool from '../tools/Tool'
import RawMaterial from '../parts/RawMaterial'
import Part from '../parts/Part'
import { useSmartTranslation } from '../../generic/hooks/useSmartTranslation'
import { DefaultAccordion } from '../../generic/DefaultAccordion'
import AlertUI from '../../generic/AlertUI'
import MoveUpIcon from '@mui/icons-material/MoveUp'
import VerticalResourceAssembly from '../../management/VerticalResourceAssembly'

const DeviceSetResource = (props) => {
  const { open, setOpen, deviceName, setDeviceName } = props
  const { t_ } = useSmartTranslation()
  const [devices, setDevices] = useState([])
  const [resourceOnDevice, setResourceOnDevice] = useState([])
  const [alertElement, showAlert] = AlertUI()
  const [alertRemoveMenu, showAlertRemoveMenu] = AlertUI()
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)

  const onDispacthSelectedData = (oldState, action) => {
    if (action.type === 'TYPE_SELECTED') {
      return { ...oldState, type: getResourceTypes().find((item) => item.name === action.value), references: [], reference: null, resource: null }
    }
    if (action.type === 'REFERENCES_LOADED') {
      return { ...oldState, references: action.value }
    }
    if (action.type === 'REFERENCE_SELECTED') {
      return { ...oldState, reference: oldState.references.find((item) => item.name === action.value), dialogOpen: true, resource: null }
    }
    if (action.type === 'RESOURCE_SELECTED') {
      return { ...oldState, selectedResource: action.value }
    }

    if (action.type === 'CLOSE_RESOURCE_SELECT') {
      return { ...oldState, dialogOpen: false, selectedResource: null }
    }
    if (action.type === 'ACCEPT_RESOURCE_SELECT') {
      return { ...oldState, dialogOpen: false, resource: oldState.selectedResource }
    }
    if (action.type === 'ALTERNATE_FIND_FIXTURE') {
      return { ...oldState, openFixtureDialog: true, findFixtureReference: action.value }
    }
    if (action.type === 'ALTERNATE_FIXTURE_SET') {
      return { ...oldState, alternateFixture: action.value }
    }
    if (action.type === 'RESET') {
      return { type: null, references: [], selectedResource: null, resource: null, dialogOpen: false, alternateFixture: null, fixture: null }
    }

    if (action.type === 'RESOURCE_LOADED') {
      return { ...oldState, type: action.value?.type, reference: action.value?.reference, resource: action.value }
    }

    return oldState
  }

  const onDispatchResourceRemoveStatus = (state, action) => {
    if (action.type === 'OPEN') {
      return { ...state, open: true, selectedResource: null, selectedIndex: null }
    }
    if (action.type === 'CLOSE') {
      return { ...state, open: false }
    }
    if (action.type === 'SET_SELECTED') {
      return { ...state, selectedResource: action.selectedResource, selectedIndex: action.selectedIndex }
    }
    if (action.type === 'SHOW_ERROR') {
      return
    }
    if (action.type === 'ACCEPT') {
      return { ...state, acceptedRemoveResource: action.value, open: false }
    }
    return state
  }

  const [resourceRemoveStatus, dispatchResourceRemoveStatus] = useReducer(onDispatchResourceRemoveStatus, { open: false, resources: null, selectedIndex: null, selectedResource: null, selectedPositionId: null, acceptedRemoveResource: null })

  const onAcceptResourceRemove = async () => {
    let resourceToRemove = resourceRemoveStatus.selectedResource
    let resourceStillOnDevice = null
    if (resourceOnDevice.length === 1) {
      resourceToRemove = resourceOnDevice[0]
    }
    if (resourceOnDevice.length > 1) {
      if (resourceToRemove) {
        const index = resourceOnDevice.findIndex((item) => item.type.name === resourceToRemove.type.name && item.id === resourceToRemove.id)
        if (index + 1 < resourceOnDevice.length) {
          resourceStillOnDevice = resourceOnDevice[index + 1]
        }
      } else {
        resourceStillOnDevice = resourceOnDevice[0]
      }
    }
    if (resourceToRemove === null) {
      showAlertRemoveMenu({ title: t_('At least one item must be selected'), severity: 'error', message: t_('Please select an item') })
      return
    }
    dispatchResourceRemoveStatus({ type: 'ACCEPT', value: resourceToRemove })
    dispatchEditingData({ type: 'RESOURCE_LOADED', value: resourceStillOnDevice })
  }

  const onAlternateFixtureSelected = async () => {
    if (!editingData.alternateFixture) {
      return
    }

    await checkAndRemoveIfRequired()

    const resource = { ...editingData.resource, type: editingData.type }
    await apiPost('device/setResourceInDevice/' + device.id + '/' + editingData.alternateFixture.id, resource).then((data) => {
      setOpen(false)
    }).catch(async (error, data) => {
      showAlert({
        title: t_('Could not set resource in position'),
        message: t_(error.message),
        severity: 'error'
      })
    })
  }

  const [editingData, dispatchEditingData] = useReducer(onDispacthSelectedData, { type: null, references: [], selectedResource: null, resource: null, dialogOpen: false, alternateFixture: null, fixture: null })

  const device = useMemo(() => {
    if (!devices || !deviceName) {
      return null
    }
    return devices.find((device) => device.name === deviceName)
  }, [deviceName, devices])

  useEffect(() => {
    const loadReferences = async () => {
      const references = await getResourcesByType(editingData.type.name, false)
      dispatchEditingData({ type: 'REFERENCES_LOADED', value: references })
    }

    if (!editingData.type) {
      return
    }

    loadReferences()
  }, [editingData.type])

  useEffect(() => {
    const loadDevices = async () => {
      setDevices(await apiGet('devicesBasicData/noStorage'))
    }
    loadDevices()
  }, [])

  const loadResourcesOnDevice = useCallback(async () => {
    if (!device) {
      return
    }
    const resources = await apiGet('devices/resources/' + device.id)
    dispatchEditingData({ type: 'RESOURCE_LOADED', value: resources.length > 0 ? resources[0] : null })
    setResourceOnDevice(resources)
  }, [device])

  useEffect(() => {
    loadResourcesOnDevice()
  }, [loadResourcesOnDevice])

  useEffect(() => {
    const loadReferences = async () => {
      if (!device) {
        return
      }
      dispatchEditingData({ type: 'REFERENCES_LOADED', value: await apiGet('devices/resources/' + device.id) })
    }
    loadReferences()
  }, [devices, device])

  const onResourceSelected = (resource) => {
    dispatchEditingData({ type: 'RESOURCE_SELECTED', value: resource })
  }

  const getResourceSelectTable = () => {
    if (editingData.type?.name === 'Part') {
      return <Part partReference={editingData.reference} hideColumns forceLoad setSelectedPart={onResourceSelected} />
    } else if (editingData.type?.name === 'Fixture') {
      return <Fixture referenceName={editingData.reference?.name} parameter={editingData.reference?.id} endpoint='fixtures' hideColumns onRowClick={onResourceSelected} />
    } else if (editingData.type?.name === 'Tool') {
      return <Tool row={editingData.reference} endpoint='tools' hideColumns units='' onRowClick={onResourceSelected} />
    } else if (editingData.type?.name === 'RawMaterial') {
      return <RawMaterial reference={editingData.reference} endpoint='rawMaterials' hideColumns onRowClick={onResourceSelected} />
    }
  }

  const checkAndRemoveIfRequired = async () => {
    // Remove resource firts if neccesary
    if (resourceRemoveStatus.acceptedRemoveResource) {
      try {
        await apiPost('deviceRemoveResource', resourceRemoveStatus.acceptedRemoveResource)
      } catch (error) {
        showAlert({ title: t_('Error trying to remove resource'), severity: 'error', message: t_(error.message) })
      }
      return true
    }
    return false
  }

  const onAccept = async () => {
    let resources = resourceOnDevice

    if ((await checkAndRemoveIfRequired())) {
      // Reload resources on device
      resources = await apiGet('devices/resources/' + device.id)
    }

    // If the type and resource is the same nothing to do
    if (resources.length === 0 && !editingData.resource) {
      setOpen(false)
      return
    }

    if (resources.length > 0 && resources[0].type.name === editingData.type.name && resources[0].id === editingData.resource.id) {
      setOpen(false)
      return
    }

    const resource = { ...editingData.resource }
    resource.type = editingData.type
    resource.reference = editingData.reference

    return apiPost('device/setResourceInDevice/' + device.id, resource).then((data) => {
      loadResourcesOnDevice()
      setOpen(false)
    }).catch(async (error, data) => {
      if (error.status !== 409) {
        showAlert({
          title: '',
          message: error.message,
          severity: 'warning'
        })
        return
      }
      const reference = await apiPost('storageGetDefaultFixtureReferenceForResource', resource)
      if (!reference) {
        showAlert({
          title: '',
          message: t_('Could not find suitable Fixture Reference'),
          severity: 'error'
        })
        return
      }
      dispatchEditingData({ type: 'ALTERNATE_FIND_FIXTURE', value: reference })
    })
  }

  useEffect(() => {
    if (!open) {
      return
    }
    // RESET All data
    dispatchEditingData({ type: 'RESET' })
    setShowConfirmDialog(false)
    loadResourcesOnDevice()
  }, [open, loadResourcesOnDevice])

  if (!devices) {
    return
  }

  return (

    <SmartDialog
      setOpen={open}
      title={<></>}
      cancelCallback={() => setOpen(false)}
      acceptCallback={() => setShowConfirmDialog(true)}
      renderComponent={
        <div style={{ marginTop: '16px' }}>
          {alertElement}
          <DefaultAccordion title={t_('Update resource in asset')}>
            <Stack alignItems='center' direction='row' sx={{ margin: '1rem' }} gap={8}>
              <Stack alignItems='center' direction='row' gap={1}>
                <Typography>{t_('Resource')}</Typography>
                <SmartSelect
                  label={t_('Type')}
                  value={editingData.type?.name}
                  minWidth='10rem'
                  selectableOptions={getResourceTypes().map(item => item.label)} onChange={(item) => dispatchEditingData({ type: 'TYPE_SELECTED', value: item })}
                />
                <SmartSelect
                  label={t_('Reference')}
                  value={editingData.reference?.name}
                  minWidth='10rem'
                  selectableOptions={editingData.references.map((item) => item.name)} onChange={(item) => dispatchEditingData({ type: 'REFERENCE_SELECTED', value: item })}
                />
                <TextField
                  label={t_('Code')}
                  value={editingData.resource ? editingData.resource.code : ''}
                  // inputProps={{ readOnly: true }}
                  InputLabelProps={{ shrink: true }}
                />
                <IconButton onClick={() => { dispatchResourceRemoveStatus({ type: 'OPEN' }) }} disabled={!resourceOnDevice || resourceOnDevice.length === 0}>
                  <MoveUpIcon />
                </IconButton>
              </Stack>
              <Stack alignItems='center' direction='row' gap={1}>
                <Typography>{t_('To')}</Typography>
                <SmartSelect
                  label={t_('Asset')}
                  value={deviceName}
                  minWidth='10rem'
                  selectableOptions={devices.map(device => device.name)} onChange={setDeviceName}
                />
              </Stack>
            </Stack>

          </DefaultAccordion>

          {resourceRemoveStatus.open && resourceOnDevice
            ? <SmartDialog
                setOpen={resourceRemoveStatus.open}
                message={resourceOnDevice.length > 1 ? t_('Select the resource(s) you want to remove') : t_('Are you sure you want to remove the fixture') + " '" + resourceOnDevice[0].code + "' ?"}
                cancelCallback={() => dispatchResourceRemoveStatus({ type: 'CLOSE' })}
                acceptCallback={onAcceptResourceRemove}
                renderComponent={
                  <Stack gap={3} alignItems='center'>
                    {alertRemoveMenu}
                    {resourceOnDevice.length > 1
                      ? <VerticalResourceAssembly
                          items={resourceOnDevice}
                          showAll
                          selectedIndex={resourceRemoveStatus.selectedIndex}
                          onResourceClick={(resource, index) => dispatchResourceRemoveStatus({ type: 'SET_SELECTED', resource, selectedIndex: index, selectedResource: resource })}
                        />
                      : null}
                  </Stack>
          }
              />
            : null}

          {editingData.findFixtureReference
            ? <SmartDialog
                setOpen={editingData.findFixtureReference}
                acceptCallback={onAlternateFixtureSelected} cancelCallback={() => dispatchEditingData({ type: 'FIXTURE_CLOSE' })} close={false}
                renderComponent={<Fixture referenceName={editingData.findFixtureReference?.name} parameter={editingData.findFixtureReference?.id} endpoint='fixturesAvailableByReference' hideColumns onRowClick={(row) => dispatchEditingData({ type: 'ALTERNATE_FIXTURE_SET', value: row })} />}
                title={
                    {
                      icon:
              <Stack
                direction='row'
                justifyContent='center'
                alignItems='center'
              >
                <StorageRounded color='primary' />
                <Typography variant='h5' color='primary' sx={{ marginLeft: '0.3em' }}>{deviceName}</Typography>
              </Stack>,
                      render: <h4>{t_('Select the Fixture to set')}</h4>
                    }
                  }
              />
            : null}

          {editingData.dialogOpen
            ? <SmartDialog
                setOpen={editingData.dialogOpen}
                acceptCallback={() => dispatchEditingData({ type: 'ACCEPT_RESOURCE_SELECT' })} cancelCallback={() => dispatchEditingData({ type: 'CLOSE_RESOURCE_SELECT' })} close={false}
                renderComponent={getResourceSelectTable()}

              />
            : null}
          {showConfirmDialog
            ? <SmartDialog
                setOpen={showConfirmDialog}
                acceptCallback={onAccept} close={false}
                cancelCallback={() => setShowConfirmDialog(false)}
                renderComponent={<div style={{ padding: '1rem' }}>{t_('Are you sure you want to move the resource?')}</div>}
              />
            : null}

        </div>
  }
    />
  )
}

export default DeviceSetResource
